<template>
  <div>
    <h1 class="mb-3 text-xs-center printMe">Online Service E-Sign Disclosure and Consent</h1>
    <p>
      This Online Service E-Sign Disclosure and Consent (“
      <strong>Agreement</strong>”) allows us to provide you with electronic
      versions of important notices and statements for those products, services,
      and Accounts offered through our Online Services, or a mobile application
      or mobile website used to access our Online Services. Certain laws and
      regulations require us to provide notices and disclosures to you in
      “writing”, which means you are entitled to receive it on paper. We may
      provide this information to you electronically, instead, with your prior
      consent. We also need your general consent to use electronic records and
      signatures in our relationship with you. So, before you use our Online
      Services you must review and consent to the terms outlined below.
    </p>
    <p>In this consent:</p>
    <p>
      "
      <strong>We</strong>," "
      <strong>us</strong>," "
      <strong>our</strong>," and "
      <strong>Alerus</strong>" means Alerus Financial, National Association, and
      each and every current and future affiliate of Alerus.
    </p>
    <p>
      “
      <strong>You</strong>” and “
      <strong>your</strong>” means the person giving
      this consent, and also each additional account owner, authorized signer,
      authorized representative, delegate, product owner and/or service user
      identified on any Alerus Product that you apply for, use or access.
    </p>
    <p>
      “
      <strong>Access Device</strong>” means any electronic device you use to
      access Online Services or view electronic documents. This includes (without
      limitation): desktop or laptop computers, or mobile devices such as tablets
      or smartphones.
    </p>
    <p>
      “
      <strong>Account</strong>” means the account(s) you have with us now or in
      the future, including ones which you open through Online Services.
    </p>
    <p>
      “
      <strong>Alerus Product</strong>” means each and every account, product or
      service we offer that you apply for, own, use, administer or access, either
      now or in the future. Alerus Products include Online Services.
    </p>
    <p>
      “
      <strong>Communications</strong>” means each disclosure, notice, agreement,
      fee schedule, statement, record, document, and other information we provide
      to you, or that you sign or submit or agree to at our request.
    </p>
    <p>
      “
      <strong>Online Services</strong>” means each and every product and service
      we offer that you apply for, use, administer or access using the Internet,
      a website, email, messaging services (including text messaging) and/or
      software applications (including applications for mobile or hand-held
      devices), either now or in the future.
    </p>
    <p>
      The words “include" and "including," when used at the beginning of a list
      of one or more items, indicates that the list contains examples – the list
      is not exclusive or exhaustive, and the items in the list are only
      illustrations. They are not the only possible items that could appear in
      the list.
    </p>
    <p>
      <strong>
        1. Your consent to use electronic records and signatures; Choosing to
        receive Communications electronically or in writing; Certain
        information must still be provided in writing.
      </strong>
    </p>
    <p>
      In our sole discretion, the Communications we provide to you, or that you
      sign or agree to at our request, may be in electronic form (“
      <strong>Electronic Records</strong>”). We may also use electronic
      signatures and obtain them from you as part of our transactions with you.
      Your consent to receive Communications in electronic form includes, but is
      not limited to:
    </p>
    <p>
      · All legal and regulatory disclosures and communications associated with
      the Account or the Online Services (for example, legally required
      notification of changes to terms and conditions related to your Account(s)
      or the Online Services.
    </p>
    <p>
      · Notices or disclosures about a change in the terms of your Account or
      associated payment feature and responses to claims.
    </p>
    <ul>
      <li>Privacy policies and notices.</li>
    </ul>
    <p>
      · Monthly (or other periodic) billing or account statements for your
      Accounts(s) or such other Communications we may include from time to time
      as part of the enrollment in electronic delivery.
    </p>
    <p>
      Electronic Records may be delivered to you in a variety of ways. These
      various delivery methods are described in this Agreement and in other
      agreements we may have with you from time to time. In some cases, you will
      be able to choose whether to receive certain Communications electronically,
      or on paper, or both. We will provide you with instructions on how to make
      those choices when they are available.
    </p>
    <p>
      We may always, in our sole discretion, provide you with any Communication
      in writing, even if you have chosen to receive it electronically.
    </p>
    <p>
      Sometimes the law, or our agreement with you, requires you to give us a
      written notice. You must still provide these notices to us on paper, unless
      we specifically tell you in another Communication how you may deliver that
      notice to us electronically.
    </p>
    <p>
      There are certain Communications that by law we are not permitted to
      deliver to you electronically, even with your consent. So long as required
      by law, we will continue to deliver those Communications to you in writing.
      However, if the law changes in the future and permits any of those
      Communications to be delivered as Electronic Records, this consent will
      automatically cover those Communications as well.
    </p>
    <p>
      We will continue to provide your tax statements on paper unless you
      separately elect to receive them electronically (if available).
    </p>
    <p>
      <strong>2. Your option to receive paper copies.</strong>
    </p>
    <p>
      If we provide Electronic Records to you, and you want a paper copy, you may
      contact the appropriate customer service unit and request a paper version.
      You will find the appropriate contact information in the account statement
      or agreement for the Alerus Product related to the Electronic Record. You
      may have to pay a fee for the paper copy unless charging a fee is
      prohibited by applicable law. Please refer to the applicable agreement for
      any fee that may apply for paper copies.
    </p>
    <p>
      <strong>3. Your consent covers all Alerus Products; Privacy Policies.</strong>
    </p>
    <p>
      Your consent covers all Communications relating to any Alerus Product. Your
      consent remains in effect until you give us notice that you are withdrawing
      it.
    </p>
    <p>
      From time to time, you may seek to obtain a new Alerus Product from us.
      When you do, we may remind you that you have already given us your consent
      to use Electronic Records and signatures. If you decide not to use
      Electronic Records and signatures in connection with the new product or
      service, your decision does not mean you have withdrawn this consent for
      any other Alerus Product.
    </p>
    <p>
      You agree that we may satisfy our obligation to provide you with an annual
      copy of our Privacy Policy by keeping it available for review on
      www.alerus.com, or, with prior notice to you, on another website where we
      offer Online Services.
    </p>
    <p>
      <strong>
        4. You may withdraw your consent at any time; Consequences of
        withdrawing consent; How to give notice of withdrawal.
      </strong>
    </p>
    <p>
      You have the right to withdraw your consent at any time. Please be aware,
      however, that withdrawal of consent may result in the termination of:
    </p>
    <ul>
      <li>your access to our Online Services, including online banking, and</li>
      <li>your ability to use certain Alerus Products.</li>
    </ul>
    <p>
      Your withdrawal of consent will become effective after we have had a
      reasonable opportunity to act upon it.
    </p>
    <p>
      If you are receiving online account statements, the termination will cause
      paper statements to be mailed to you via the U.S. Postal Service or other
      courier. Depending on the specific Alerus Product, if you withdraw consent
      we may charge higher or additional fees for that product or for services
      related to it. Please refer to the applicable agreement for any fee that
      may apply.
    </p>
    <p>To withdraw your consent, you must contact us at 833-325-3787</p>
    <p>
      <strong>5. You must keep your email or electronic address current with us.</strong>
    </p>
    <p>
      You must promptly notify us of any change in your email or other electronic
      address. You may change the email address on record for you by going to the
      Account Services page within your online service and updating your contact
      information. If you have given us another type of electronic address, such
      as an electronic address or mobile phone number for SMS text messages, you
      may change that address by going to the account management page for each
      Alerus Product you have linked to that address. There may be other, special
      Alerus Products for which we provide separate instructions to update your
      email or other electronic address.
    </p>
    <p>
      <strong>6. Hardware and software you will need.</strong>
    </p>
    <p>
      <em>
        To access Online Services and Electronic Records on a traditional
        computer, you will need:
      </em>
    </p>
    <p>
      · A computer with certain browsers that are up-to-date, have a high
      security standard, and are compatible with and supported by your operating
      system.
    </p>
    <p>
      · An internet connection with an internet browser that is up-to-date, has a
      high security standard and is compatible with and supported by your
      operating system.
    </p>
    <p>
      · Sufficient electronic storage capacity on your computer's hard drive or
      other data storage unit, or a printer, if you wish to print or retain any
      electronic documents.
    </p>
    <p>· An e-mail account with an internet service provider and e-mail software</p>
    <p>
      · Software that accurately reads and displays .pdf files (such as a current
      version of Adobe Reader)
    </p>
    <p>
      <em></em>
    </p>
    <p>
      <em>
        To access Online Services and Electronic Records on a mobile device,
        you will need:
      </em>
    </p>
    <p>
      · A mobile device with any of the following operating systems: Android or
      iOS
    </p>
    <ul>
      <li>A data plan provided by your mobile carrier.</li>
    </ul>
    <p>
      · To access Online Services and Electronic Records at www.alerus.com, a
      mobile browser that is compatible with and supported by your operating
      system (such as Chrome or Safari).
    </p>
    <p>
      · To access Online Services and Electronic Records through one of our
      mobile applications, you will need to download the applicable Alerus
      application at your respective app store.
    </p>
    <p>
      · If you wish to view .pdf files on your mobile device, software that
      accurately reads and displays .pdf files (such as the mobile version of
      Adobe Reader).
    </p>
    <p>
      You should check these terms occasionally for updates on supported
      software. From time to time, we may offer services or features that require
      that your internet browser be configured in a particular way, such as
      permitting the use of JavaScript or cookies. If we detect that your
      internet browser is not properly configured, we will provide you with a
      notice and advice on how to update your configuration.
    </p>
    <p>
      We reserve the right to discontinue support of a current version of
      software if, in our sole opinion, it suffers from a security flaw or other
      flaw that makes it unsuitable for use with Online Services.
    </p>
    <p>
      <strong>7. Changes to hardware or software requirements.</strong>
    </p>
    <p>
      If our hardware or software requirements change, and that change would
      create a material risk that you would not be able to access or retain your
      Electronic Records, we will give you notice of the revised hardware or
      software requirements. Continuing to use Online Services after receiving
      notice of the change is reaffirmation of your consent.
    </p>
    <p>
      <strong>8. Multiple Access Devices.</strong>
    </p>
    <p>
      Your acceptance of this agreement on one Access Device constitutes your
      acceptance on all Access Devices you use. For example, if you view and
      accept this agreement on a tablet, the terms of this Agreement will apply
      to Online Services and Electronic Records accessed on a desktop computer
      (or vice versa).
    </p>
    <p>
      Additionally, by viewing and accepting this agreement on any Access Device,
      you are reasonably demonstrating your ability to access Online Services and
      view Electronic Records in the format that the services are provided on
      that Access Device, and all subsequent Access Devices. If you change Access
      Devices (or use multiple Access Devices), it is your responsibility to
      ensure the new Access Device meets the applicable system requirements and
      that you are still able to access Online Services and view Electronic
      Records on the subsequent Access Device. Your continued use of Online
      Services on other Access Devices is your reaffirmation of this Agreement.
    </p>
    <p>
      <strong>9. Your enrollment in Online Services using our equipment.</strong>
    </p>
    <p>
      If you enroll for an Online Service through one of our customer service
      representatives, or using our equipment, your enrollment may not be
      complete until you take additional action. We will advise you, at the time
      of your enrollment, of any additional action you must take. If you take the
      required action, it is an affirmation of your consent to use electronic
      records and signatures under the terms of this E-SIGN Consent.
    </p>
    <p>
      <strong>10. Communications in languages other than English.</strong>
    </p>
    <p>
      Please note, we may be unable to fulfill and service Alerus products or
      services in a language other than English. Future Communications may be in
      English only. If you are not fluent in English, you should consider
      obtaining the services of an interpreter or taking other steps to ensure
      you understand the transaction before entering into it and to explain any
      future Communications in English.
    </p>
    <p>
      <strong>
        By providing your consent, you are also confirming that you have the
        hardware and software described above, that you are able to receive and
        review electronic records, and that you have an active email account.
        You are also confirming that you are authorized to, and do, consent on
        behalf of all the other account owners, authorized signers, authorized
        representatives, delegates, product owners and/or service users
        identified with your Alerus Products.
      </strong>
    </p>
  </div>
</template>
<script>
export default {
  name: "consent",
  props: {
    title: Boolean
  },
  data() {
    return {};
  },
  methods: {}
};
</script>
<style lang="scss">
.printMe {
  display: none;
}
@media print {
  .printMe {
    display: block;
  }
}
</style>
